import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faSliders } from '@fortawesome/pro-regular-svg-icons/faSliders';
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons/faCircleXmark';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { Card } from 'Card';
import { type CareerProfile } from 'CareerProfiles';
import { getProgramConfigValue } from 'Program';
import { LoadMoreButton } from '../components/LoadMoreButton';
import { NetworkErrorCard } from '../components/NetworkErrorCard';
import { ScrollToTopButton } from '../components/ScrollToTopButton';
import { useMessageUser } from '../hooks/useMessageUser';
import { ScrollFadeContainer } from '../components/ScrollFadeContainer';
import { useScrollFade } from '../hooks/useScrollFade';
import { ProfileCard } from '../components/people/ProfileCard';
import { ProfileDetail } from '../components/people/ProfileDetail';
import { PeopleFiltersSidebar } from '../components/people/PeopleFiltersSidebar';
import { FindMoreStudentsSidebar } from '../components/people/FindMoreStudentsSidebar';
import { MobileFiltersScreen } from '../components/people/MobileFiltersScreen';
import { DesktopFiltersModal } from '../components/people/DesktopFiltersModal';
import { ContactFormModal } from '../components/people/ContactFormModal';
import { useStudentProfiles } from '../hooks/useStudentProfiles';
import { usePeopleFilters } from '../hooks/usePeopleFilters';
import { useGetStudentNetworkProfileByUserIdQuery } from '../api/studentNetworkProfilesApi';
import { useNetworkOutletContext } from '../StudentNetworkV2';
import { useCrossfade } from '../hooks/useCrossfade';
import '../locales/studentNetworkV2/studentNetworkV2-en.json';
import 'Careers/locales/careers/field_options-en.json';

function getProfileSidebarData(profile: CareerProfile, tIndustry: (key: string) => string) {
    const location = [profile.city, profile.state, profile.country].filter(Boolean).join(', ') || undefined;
    const company =
        profile.workExperiences?.[0]?.professionalOrganization?.text ??
        profile.workExperiences?.[0]?.professional_organization?.text ??
        undefined;
    const industryKey = profile.workExperiences?.[0]?.industry;
    const industry = industryKey && industryKey !== 'other' ? tIndustry(industryKey) : undefined;
    const program = profile.programs?.[0]
        ? (getProgramConfigValue(profile.programs[0].programType, 'shortProgramTitle') as string)
        : undefined;

    type EduExp = { educationalOrganization?: { text?: string } };
    const rawEdu = (profile as Record<string, unknown>).educationExperiences as EduExp[] | undefined;
    const school = rawEdu?.find(e => e.educationalOrganization?.text)?.educationalOrganization?.text;

    const placeDetails = profile.placeDetails ?? undefined;

    return { location, company, industry, school, program, placeDetails };
}

// oxlint-disable-next-line max-lines-per-function, complexity -- view with list/detail state and crossfade transitions
export function PeopleView() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { '*': wildcardPath } = useParams();
    const routeUserId = wildcardPath || undefined;
    const navigate = useNavigate();
    const initialUserIds = useMemo(
        () => {
            const ids = searchParams.get('ids');
            return ids ? ids.split(',').filter(Boolean) : [];
        },
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- read once on mount
        [],
    );
    const initialMyClass = useMemo(
        () => searchParams.has('myClass'),
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- read once on mount
        [],
    );
    // Strip the myClass param from the URL after reading it
    useEffect(() => {
        if (initialMyClass && searchParams.has('myClass')) {
            searchParams.delete('myClass');
            setSearchParams(searchParams, { replace: true });
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
    }, []);
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.people' });
    const { t: tFilters } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.filters' });
    const { t: tIndustry } = useTranslation('back_royal', { keyPrefix: 'careers.field_options' });
    const {
        filters,
        filterState,
        applyFilterState,
        hasActiveFilters,
        activeFilterCount,
        resetFilters: resetFiltersBase,
        keywordSearch,
        setKeywordSearch,
        activeTab,
        setActiveTab,
        showAdvanced,
        setShowAdvanced,
        places,
        setPlaces,
        programTypes,
        setProgramTypes,
        companies,
        setCompanies,
        industries,
        setIndustries,
        interests,
        setInterests,
        lookingFor,
        setLookingFor,
        timezones,
        setTimezones,
    } = usePeopleFilters({ initialUserIds, initialMyClass });

    const resetFilters = useCallback(() => {
        resetFiltersBase();
        if (searchParams.has('ids')) {
            searchParams.delete('ids');
            setSearchParams(searchParams, { replace: true });
        }
    }, [resetFiltersBase, searchParams, setSearchParams]);

    const { profiles, totalCount, isLoading, isFetching, isError, error, loadMore, hasMore, retry } =
        useStudentProfiles(filters);
    const [selectedProfile, setSelectedProfile] = useState<CareerProfile | null>(null);
    // Desktop crossfade: when transitioning, render both views overlaid and
    // simultaneously fade old out + new in for a smooth blend with no blank gap.
    const { fadeStyle, startCrossfade } = useCrossfade();
    const { fadeStyle: filtersFadeStyle, startCrossfade: startFiltersCrossfade } = useCrossfade();
    const deepLinkAppliedRef = useRef(false);

    // Deep link: /people/:userId fetches a single profile and auto-selects it
    const { data: deepLinkProfile } = useGetStudentNetworkProfileByUserIdQuery(routeUserId!, { skip: !routeUserId });

    useEffect(() => {
        if (routeUserId && deepLinkProfile && !deepLinkAppliedRef.current) {
            deepLinkAppliedRef.current = true;
            setSelectedProfile(deepLinkProfile);
        }
        if (!routeUserId) deepLinkAppliedRef.current = false;
    }, [routeUserId, deepLinkProfile]);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showDesktopFilters, setShowDesktopFilters] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const savedScrollRef = useRef(0);
    const { fadeTop: showScrollTop } = useScrollFade(scrollRef, 100);
    const scrollToTop = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);
    const { setMobileNavHidden } = useNetworkOutletContext();

    useEffect(() => {
        setMobileNavHidden(!!selectedProfile);
        return () => setMobileNavHidden(false);
    }, [selectedProfile, setMobileNavHidden]);

    const selectProfile = useCallback(
        (profile: CareerProfile) => {
            if (scrollRef.current) savedScrollRef.current = scrollRef.current.scrollTop;
            // Set deepLinkAppliedRef before navigating so the route-change effect
            // doesn't re-fetch and overwrite the profile we already have in hand.
            deepLinkAppliedRef.current = true;
            const userId = profile.userId;
            const qs = searchParams.toString();
            navigate(`/student-network/people/${userId}${qs ? `?${qs}` : ''}`, { replace: true });
            startCrossfade(() => {
                setSelectedProfile(profile);
                requestAnimationFrame(() => {
                    if (scrollRef.current) scrollRef.current.scrollTop = 0;
                });
            });
        },
        [navigate, searchParams, startCrossfade],
    );

    const deselectProfile = useCallback(() => {
        const qs = searchParams.toString();
        navigate(`/student-network/people${qs ? `?${qs}` : ''}`, { replace: true });
        startCrossfade(() => {
            setSelectedProfile(null);
            requestAnimationFrame(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = savedScrollRef.current;
            });
        });
    }, [navigate, searchParams, startCrossfade]);

    const handleFindMoreSearch = useCallback(
        (type: string, value: string) => {
            // Reset filters and navigate to /people in a single step
            // (avoids the double-navigation from resetFilters' setSearchParams)
            resetFiltersBase();

            if (type === 'location') {
                // Use the profile's placeDetails for proximity-based city search
                const pd = selectedProfile?.placeDetails;
                if (pd) setPlaces([pd]);
                else setKeywordSearch(value);
            } else if (type === 'company') {
                setCompanies([value]);
                setShowAdvanced(true);
            } else if (type === 'industry') {
                const rawKey = selectedProfile?.workExperiences?.[0]?.industry;
                if (rawKey) setIndustries([rawKey]);
                setShowAdvanced(true);
            } else if (type === 'program') {
                // Find the programType key matching this label
                const programType = selectedProfile?.programs?.find(p => {
                    const label = getProgramConfigValue(p.programType, 'shortProgramTitle') as string;
                    return label === value;
                })?.programType;
                if (programType) setProgramTypes([programType]);
            } else {
                // school — use keyword search
                setKeywordSearch(value);
            }

            setSelectedProfile(null);
            navigate('/student-network/people', { replace: true });
            requestAnimationFrame(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = 0;
            });
        },
        [
            resetFiltersBase,
            navigate,
            setPlaces,
            setCompanies,
            setShowAdvanced,
            setIndustries,
            setProgramTypes,
            setKeywordSearch,
            selectedProfile,
        ],
    );

    const { handleMessage, contactFormProfile, closeContactForm } = useMessageUser();

    return (
        <div className="relative flex h-full overflow-clip px-0 md:gap-7.5 md:px-0">
            {/* Mobile filters overlay — absolute within PeopleView so it respects bottom nav */}
            {showMobileFilters && (
                <div className="absolute inset-0 z-50 bg-white md:hidden" style={filtersFadeStyle}>
                    <MobileFiltersScreen
                        filterState={filterState}
                        onApply={applyFilterState}
                        onClose={() => startFiltersCrossfade(() => setShowMobileFilters(false))}
                    />
                </div>
            )}
            {/* Contact form modal — email fallback when Sendbird isn't available */}
            {contactFormProfile && <ContactFormModal profile={contactFormProfile} onClose={closeContactForm} />}
            {/* Desktop filters modal — shown at md-to-lg widths when sidebar is hidden */}
            {showDesktopFilters && (
                <DesktopFiltersModal
                    filterState={filterState}
                    onApply={applyFilterState}
                    onClose={() => setShowDesktopFilters(false)}
                />
            )}
            {/* Main column: scrollable list OR scrollable detail — full width on mobile, 600px on desktop */}
            <ScrollFadeContainer
                externalRef={scrollRef}
                className="hide-scrollbar w-full overflow-y-auto min-[1200px]:min-w-[450px] min-[1200px]:max-w-[700px] min-[1200px]:flex-1"
                fadeOffset={!selectedProfile && window.innerWidth < 768 ? 60 : 0}
                style={fadeStyle}
            >
                <ScrollToTopButton
                    visible={showScrollTop && !selectedProfile}
                    onClick={scrollToTop}
                    className="top-20 md:top-8"
                />
                {selectedProfile ? (
                    /* Detail view — own scroll starting at top */
                    <>
                        {/* Mobile: continuous white bg, no card wrapper */}
                        <div className="flex min-h-full flex-col md:hidden">
                            <ProfileDetail
                                profile={selectedProfile}
                                onBack={deselectProfile}
                                onMessage={() => handleMessage(selectedProfile)}
                            />
                        </div>
                        {/* Desktop: card wrapper */}
                        <div className="hidden flex-col gap-[15px] pb-5 md:flex">
                            <Card padding={0} radius={8}>
                                <ProfileDetail
                                    profile={selectedProfile}
                                    onBack={deselectProfile}
                                    onMessage={() => handleMessage(selectedProfile)}
                                />
                            </Card>
                        </div>
                    </>
                ) : (
                    /* List view */
                    <>
                        {/* Mobile: continuous white surface with search + list */}
                        <div className="flex min-h-full flex-col bg-white md:hidden" style={filtersFadeStyle}>
                            {/* Mobile search bar */}
                            <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 pb-2 pt-1">
                                <div className="flex h-12 flex-1 items-center gap-2 rounded-[8px] bg-slate-grey-50 px-3">
                                    <FontAwesomeIcon icon={faMagnifyingGlass} className="text-base text-slate-grey" />
                                    <input
                                        type="text"
                                        value={keywordSearch}
                                        onChange={e => setKeywordSearch(e.target.value)}
                                        placeholder={tFilters('search')}
                                        className="flex-1 bg-transparent text-base text-black outline-none placeholder:text-slate-grey"
                                    />
                                    {keywordSearch && (
                                        <button
                                            type="button"
                                            onClick={() => setKeywordSearch('')}
                                            className="shrink-0 text-lg text-slate-grey-300 hover:text-slate-grey"
                                        >
                                            <FontAwesomeIcon icon={faCircleXmark} className="text-base" />
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => startFiltersCrossfade(() => setShowMobileFilters(true))}
                                    className="relative flex h-12 w-12 items-center justify-center rounded-[8px] bg-slate-grey-50"
                                >
                                    <FontAwesomeIcon icon={faSliders} className="text-lg text-black" />
                                    {activeFilterCount > 0 && (
                                        <span className="absolute -bottom-1.5 -end-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Mobile profile list — continuous rows with dividers */}
                            {isLoading ? (
                                <div className="flex items-center justify-center p-12">
                                    <FrontRoyalSpinner className="no-delay no-top-margin" color="force-brand" />
                                </div>
                            ) : isError && profiles.length === 0 ? (
                                <NetworkErrorCard error={error} onRetry={retry} />
                            ) : profiles.length === 0 ? (
                                <div className="px-5 py-8 text-center text-sm italic text-slate-grey">
                                    {t('noResults')}
                                </div>
                            ) : (
                                <>
                                    {profiles.map((profile, i) => (
                                        <div key={profile.id}>
                                            {i > 0 && <div className="mx-5 h-px bg-slate-grey-100" />}
                                            <ProfileCard
                                                profile={profile}
                                                onClick={() => selectProfile(profile)}
                                                onMessage={() => handleMessage(profile)}
                                            />
                                        </div>
                                    ))}
                                    <LoadMoreButton
                                        hasMore={hasMore}
                                        isFetching={isFetching}
                                        remaining={totalCount - profiles.length}
                                        onClick={loadMore}
                                        error={isError && profiles.length > 0}
                                        onRetry={retry}
                                    />
                                </>
                            )}
                        </div>

                        {/* Hybrid search bar — visible at md-to-lg when sidebar is hidden */}
                        <div className="mb-[15px] hidden md:block min-[1200px]:hidden">
                            <Card padding={0} radius={8}>
                                <div className="flex items-center gap-2 p-4">
                                    <div className="flex h-12 flex-1 items-center gap-2 rounded-[8px] bg-slate-grey-50 px-3">
                                        <FontAwesomeIcon
                                            icon={faMagnifyingGlass}
                                            className="text-base text-slate-grey"
                                        />
                                        <input
                                            type="text"
                                            value={keywordSearch}
                                            onChange={e => setKeywordSearch(e.target.value)}
                                            placeholder={tFilters('search')}
                                            className="flex-1 bg-transparent text-base text-black outline-none placeholder:text-slate-grey"
                                        />
                                        {keywordSearch && (
                                            <button
                                                type="button"
                                                onClick={() => setKeywordSearch('')}
                                                className="shrink-0 text-slate-grey-300 hover:text-slate-grey"
                                            >
                                                <FontAwesomeIcon icon={faCircleXmark} className="text-base" />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowDesktopFilters(true)}
                                        className="relative flex h-12 w-12 items-center justify-center rounded-[8px] bg-slate-grey-50"
                                    >
                                        <FontAwesomeIcon icon={faSliders} className="text-lg text-black" />
                                        {activeFilterCount > 0 && (
                                            <span className="absolute -bottom-1.5 -end-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                                                {activeFilterCount}
                                            </span>
                                        )}
                                    </button>
                                </div>
                            </Card>
                        </div>

                        {/* Desktop: separate cards with gaps */}
                        <div className="hidden flex-col gap-[15px] pb-5 md:flex">
                            {isLoading ? (
                                <Card padding={0} radius={8} className="flex-1">
                                    <div className="flex h-full items-center justify-center py-24">
                                        <FrontRoyalSpinner className="no-delay no-top-margin" color="force-brand" />
                                    </div>
                                </Card>
                            ) : isError && profiles.length === 0 ? (
                                <Card padding={0} radius={8}>
                                    <NetworkErrorCard error={error} onRetry={retry} />
                                </Card>
                            ) : profiles.length === 0 ? (
                                <Card padding={0} radius={8}>
                                    <div className="px-5 py-8 text-center text-sm italic text-slate-grey">
                                        {t('noResults')}
                                    </div>
                                </Card>
                            ) : (
                                <>
                                    {profiles.map(profile => (
                                        <Card
                                            key={profile.id}
                                            padding={0}
                                            radius={8}
                                            className="[transition:border-color_300ms] hover:!border-slate-grey-600"
                                        >
                                            <ProfileCard
                                                profile={profile}
                                                onClick={() => selectProfile(profile)}
                                                onMessage={() => handleMessage(profile)}
                                            />
                                        </Card>
                                    ))}
                                    <LoadMoreButton
                                        hasMore={hasMore}
                                        isFetching={isFetching}
                                        remaining={totalCount - profiles.length}
                                        onClick={loadMore}
                                        error={isError && profiles.length > 0}
                                        onRetry={retry}
                                    />
                                </>
                            )}
                        </div>
                    </>
                )}
            </ScrollFadeContainer>

            {/* Sidebar: filters when browsing, find-more when viewing a profile — crossfade */}
            {selectedProfile ? (
                <div style={fadeStyle}>
                    <FindMoreStudentsSidebar
                        {...getProfileSidebarData(selectedProfile, tIndustry)}
                        onSearch={handleFindMoreSearch}
                    />
                </div>
            ) : (
                <div style={fadeStyle}>
                    <PeopleFiltersSidebar
                        totalCount={totalCount}
                        isLoading={isLoading}
                        keywordSearch={keywordSearch}
                        onKeywordSearchChange={setKeywordSearch}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        showAdvanced={showAdvanced}
                        onShowAdvancedChange={setShowAdvanced}
                        places={places}
                        onPlacesChange={setPlaces}
                        programTypes={programTypes}
                        onProgramTypesChange={setProgramTypes}
                        companies={companies}
                        onCompaniesChange={setCompanies}
                        industries={industries}
                        onIndustriesChange={setIndustries}
                        interests={interests}
                        onInterestsChange={setInterests}
                        lookingFor={lookingFor}
                        onLookingForChange={setLookingFor}
                        timezones={timezones}
                        onTimezonesChange={setTimezones}
                        hasActiveFilters={hasActiveFilters}
                        onReset={resetFilters}
                    />
                </div>
            )}
        </div>
    );
}
